import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, ShieldCheck } from "lucide-react";
import { FIRM } from "@/lib/firm";
import { GoldFoilSeal } from "@/components/documents/_shared";

const Article = ({ n, title, children }) => (
  <section className="mb-5" data-testid={`charter-article-${n}`}>
    <h3 className="font-serif text-[15px] text-[#1a1a17] mb-1.5" style={{ letterSpacing: "0.02em" }}>
      ARTICLE {n}. {title}
    </h3>
    <div className="text-[#33332c] text-[12.5px] leading-relaxed space-y-2 text-justify">{children}</div>
  </section>
);

export default function CertificateOfAuthority() {
  const nav = useNavigate();
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  return (
    <div className="min-h-screen bg-[#efeee6]" data-testid="charter-page">
      <header className="no-print px-6 sm:px-10 py-4 flex items-center justify-between border-b border-[#d9d8cc] bg-[#0A0A0A]">
        <div>
          <div className="font-serif text-[19px] tracking-tight leading-none text-[#ECECE4]">SEUTRADE FINANCE</div>
          <div className="font-serif text-[10px] tracking-[0.22em] uppercase text-[#B8860B] leading-none mt-1">Financial Services</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => window.print()} data-testid="charter-print-btn"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#0A0A0A] bg-[#B8860B] hover:bg-[#c9971b] px-4 py-2 rounded-full transition-colors">
            <Printer size={13} /> Print / Save PDF
          </button>
          <button onClick={() => nav("/about")} data-testid="charter-back-btn"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#ECECE4] border border-[#2A2A22] hover:border-[#B8860B] px-4 py-2 rounded-full transition-colors">
            <ArrowLeft size={13} /> About
          </button>
        </div>
      </header>

      <main className="max-w-[850px] mx-auto px-4 py-10 print:py-0">
        <div className="bg-white border-[6px] border-double border-[#B8860B] shadow-xl p-10 sm:p-14 relative" data-testid="charter-certificate">
          {/* Gold seal */}
          <div className="absolute right-8 bottom-8 opacity-95">
            <GoldFoilSeal size={120} label="" />
          </div>

          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.34em] uppercase text-[#8a7530] mb-3">
              <ShieldCheck size={13} className="text-[#B8860B]" /> Private International Financial Organization
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#12120f] tracking-tight leading-tight">
              Charter &amp; Certificate of Authority
            </h1>
            <div className="mt-2 font-serif text-[15px] text-[#5a4a1a]">{FIRM.name}</div>
            <div className="mt-4 mx-auto w-24 border-t-2 border-[#B8860B]" />
          </div>

          <p className="text-[12.5px] text-[#33332c] leading-relaxed text-justify mb-6">
            <strong>KNOW ALL PERSONS BY THESE PRESENTS</strong>, that the undersigned trustees and authorized officers do
            hereby certify, adopt, and proclaim this Charter and Certificate of Authority, evidencing the constitution,
            existence, powers, and standing of the below-named Organization as a private international financial
            organization operating under private law and contract and the Uniform Commercial Code.
          </p>

          <Article n="I" title="Name & Domicile">
            <p>The name of the Organization is <strong>{FIRM.name}</strong> (the <em>"Organization"</em>). Its principal
            administrative office is located at {FIRM.address}, United States, with authority to maintain agencies,
            correspondents, and places of business as its officers may determine.</p>
          </Article>

          <Article n="II" title="Identity & Registration">
            <p>The Organization is identified for tax and transactional purposes by U.S. Employer Identification Number
            (Registration / EIN No.) <strong>{FIRM.ein}</strong>. Its instruments and records reference this number as
            the Organization's registration identifier. Any separate international registration or correspondent
            identifier assigned to the Organization shall be recited on the applicable instrument.</p>
          </Article>

          <Article n="III" title="Nature & Constitution">
            <p>The Organization is a <strong>privately constituted financial organization</strong> organized and existing
            under private law, its charter, and internal by-laws, administered by its trustees and authorized officers.
            It is <strong>not</strong> the United States, a State, the Federal Reserve, the U.S. Treasury, or an agency or
            instrumentality of any government, and it does not act for or on behalf of any government.</p>
          </Article>

          <Article n="IV" title="Powers & Authority">
            <p>Subject to application, approval, and applicable law, the Organization is authorized to: (1) originate,
            purchase, discount, accept, and negotiate commercial paper and negotiable instruments; (2) issue and accept
            private negotiable instruments, drafts, and funding certificates as commercial-financing instruments
            under <strong>UCC Articles 3 and 4</strong>; (3) arrange private financing, refinancing, forbearance,
            deferral, consolidation, and settlement for qualified counterparties; and (4) enter contracts, hold and
            manage reserves, and do all lawful acts incidental to the foregoing.</p>
          </Article>

          <Article n="V" title="Governing Law & International Framework">
            <p>The Organization's instruments are structured to satisfy the negotiability requirements of
            <strong> UCC Article 3</strong> and the acceptance/collection framework of <strong>UCC Article 4</strong>, and,
            where applicable, to conform to generally recognized international commercial practice — including the
            <strong> UNCITRAL Model Law on International Credit Transfers</strong>, the <strong>ICC Uniform Customs and
            Practice for Documentary Credits (UCP 600)</strong>, and the <strong>ICC Uniform Rules for Collections
            (URC 522)</strong>. Nothing herein constitutes a public solicitation of deposits or securities in any
            jurisdiction where such solicitation would be unlawful.</p>
          </Article>

          <Article n="VI" title="Disclosures">
            <p>The Organization does not accept insured retail deposits. Its instruments and obligations are
            <strong> not insured or guaranteed by the FDIC, NCUA, SIPC, or any government agency</strong>, are not legal
            tender or government obligations, and do not by delivery alone satisfy or discharge any third-party debt
            unless and until accepted funds are actually received and credited. All services are private, conditional,
            and subject to application and approval.</p>
          </Article>

          <div className="mt-8 pt-5 border-t border-[#ddd] grid grid-cols-2 gap-8 text-[12px] text-[#33332c]" data-testid="charter-execution">
            <div>
              <div className="h-9 border-b border-[#555] mb-1" />
              <div className="font-semibold">{FIRM.trustee}</div>
              <div className="text-[10.5px] text-[#666]">Trustee</div>
            </div>
            <div>
              <div className="h-9 border-b border-[#555] mb-1" />
              <div className="font-semibold">{FIRM.officer}</div>
              <div className="text-[10.5px] text-[#666]">Authorized Officer</div>
            </div>
          </div>

          <div className="mt-6 text-[11px] text-[#6d6455] leading-relaxed">
            Executed and effective as of {today}, at {FIRM.address}.
            <br />{FIRM.name} · Registration / EIN No. {FIRM.ein} · {FIRM.phone}
          </div>
        </div>

        <p className="no-print text-center text-[11px] text-[#8a8a80] mt-5 mb-10">
          This Charter &amp; Certificate of Authority evidences a private organizational charter. It is not a government
          license or a government-issued bank charter.
        </p>
      </main>
    </div>
  );
}
